import clsx from "clsx";
import {
  BORDER_SUBTLE,
  FOCUS_RING,
  ROW_HOVER,
  SURFACE_INSET,
  TEXT_BODY,
  TEXT_MUTED,
  TEXT_ROOT,
  TEXT_SUBTLE,
} from "@/components/ui/surface/surfaceTokens";

/** Semi-transparent workbench panel surface (over OS window chrome). */
export const WORKBENCH_SURFACE_CLASS =
  "bg-gradient-to-bl from-slate-100 to-slate-50 dark:from-slate-700/50 dark:to-slate-700/40";

/** Inset list panel inside a workbench surface (Artifacts app, checklist detail). */
export const WORKBENCH_LIST_SHELL_CLASS = clsx(
  "flex-1 min-h-0 overflow-y-auto rounded-2xl border ring ring-inset",
  BORDER_SUBTLE,
  "bg-gradient-to-b from-slate-50 to-white ring-slate-200/80",
  "dark:from-slate-900/30 dark:to-slate-900/40 dark:ring-white/10",
);

/** Module outer wrapper text (over OS window chrome). */
export const WORKBENCH_ROOT_TEXT_CLASS = TEXT_ROOT;

/** Primary body text on workbench surfaces. */
export const WORKBENCH_BODY_TEXT_CLASS = TEXT_BODY;

/** Secondary / helper text on workbench surfaces. */
export const WORKBENCH_MUTED_TEXT_CLASS = TEXT_MUTED;

/** Tertiary labels, timestamps, metadata. */
export const WORKBENCH_SUBTLE_TEXT_CLASS = TEXT_SUBTLE;

/** Inset card/column panel inside a workbench surface. */
export const WORKBENCH_INSET_PANEL_CLASS = clsx("rounded-xl border", BORDER_SUBTLE, SURFACE_INSET);

/** Search pill chrome in workbench toolbars. */
export const WORKBENCH_SEARCH_PILL_CLASS =
  "border border-slate-200 bg-white focus-within:border-slate-300 focus-within:ring-1 focus-within:ring-slate-200 dark:border-slate-600 dark:bg-slate-900/50 dark:focus-within:border-slate-500 dark:focus-within:ring-slate-500/25";

/** Toolbar divider under workbench header rows. */
export const WORKBENCH_TOOLBAR_DIVIDER_CLASS = "border-b border-slate-200 dark:border-white/5";

/**
 * List-card tokens — used by MissionControlCard, MissionRow, ActionQueue, ArtifactCard.
 * Prefer these over raw slate-* for secondary lines, timestamps, and card chrome.
 */

/** Metadata row under list card titles (stage, assignees, context lines). */
export const WORKBENCH_CARD_META_CLASS = "text-xs text-slate-600 dark:text-slate-400";

/** Link styling inside card meta rows. */
export const WORKBENCH_CARD_META_LINK_CLASS =
  "text-slate-700 hover:text-slate-900 hover:underline dark:text-slate-300 dark:hover:text-slate-100";

/** Timestamp on list card right column. */
export const WORKBENCH_CARD_TIMESTAMP_CLASS =
  "text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap tabular-nums text-right";

/** Muted inline suffix in card titles (e.g. template name after em dash). */
export const WORKBENCH_CARD_TITLE_SUFFIX_CLASS =
  "text-slate-600 dark:text-slate-400 font-normal";

/** Attention / warning callout on card title row. */
export const WORKBENCH_CARD_ATTENTION_CLASS = "text-xs text-amber-700 dark:text-amber-400";

/** Emphasis within card meta (e.g. stage label). */
export const WORKBENCH_CARD_META_EMPHASIS_CLASS = "text-slate-700 dark:text-slate-300";

/** Separator dot between card meta segments. */
export const WORKBENCH_CARD_META_SEP_CLASS = "text-slate-500 dark:text-slate-600";

/** Unassigned / warning meta label on cards. */
export const WORKBENCH_CARD_WARNING_META_CLASS = "text-amber-700 dark:text-amber-300/80";

/** Interactive list card shell hover/focus. */
export const WORKBENCH_CARD_SHELL_INTERACTIVE_CLASS = clsx(
  "hover:bg-slate-100/90 focus:bg-slate-100/90",
  "dark:hover:bg-white/[0.04] dark:focus:bg-white/[0.04]",
);

/** Selected interactive list card. */
export const WORKBENCH_CARD_SHELL_SELECTED_CLASS = "bg-slate-100/90 dark:bg-white/[0.06]";

/** Status badge highlight when value changes. */
export const WORKBENCH_CARD_STATUS_ADVANCING_CLASS =
  "bg-slate-200/80 ring-1 ring-slate-300/80 dark:bg-white/[0.06] dark:ring-white/10";
